import { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Maybe,
  PeriodsGridItemFragment,
  RegularNeedPeriodAttachmentsFilter,
  RegularNeedsPeriodsQueryVariables,
} from 'schema/serverTypes';
import { ColumnConfig, DataConnection } from 'hooks';
import { SortableTableColumn } from 'components/SortableTable';

type Cursor = Maybe<string> | undefined;

export interface UsePeriodsStateConfig {
  columns: ColumnConfig<PeriodsGridItemFragment>[];
  rowsPerPage?: number;
  month: string;
}

export const usePeriodsState = (config: UsePeriodsStateConfig) => {
  const { columns, rowsPerPage = 50, month } = config;

  const [search, setSearch] = useState<string | undefined>(undefined);
  const [attachments, setAttachments] = useState<RegularNeedPeriodAttachmentsFilter | undefined>(
    undefined
  );
  const [page, setPage] = useState(0);
  const [first, setFirst] = useState<number | undefined>(rowsPerPage);
  const [last, setLast] = useState<number | undefined>(undefined);
  const [after, setAfter] = useState<Maybe<string> | undefined>(null);
  const [before, setBefore] = useState<Maybe<string> | undefined>(null);

  const resetPaging = useCallback(() => {
    setPage(0);
    setAfter(null);
    setBefore(null);
    setFirst(rowsPerPage);
    setLast(undefined);
  }, [rowsPerPage]);

  useEffect(() => {
    resetPaging();
  }, [month, resetPaging]);

  const onSearch = useCallback(
    (value: string | undefined) => {
      setSearch(value);
      resetPaging();
    },
    [resetPaging]
  );

  const onAttachmentsChange = useCallback(
    (value: RegularNeedPeriodAttachmentsFilter | undefined) => {
      setAttachments(value);
      resetPaging();
    },
    [resetPaging]
  );

  const onSort = useCallback(() => {}, []);

  const onPage = useCallback(
    (newPage: number, startCursor: Cursor, endCursor: Cursor) => {
      if (newPage > page) {
        setFirst(rowsPerPage);
        setLast(undefined);
        setAfter(endCursor);
        setBefore(undefined);
      } else {
        setFirst(undefined);
        setLast(rowsPerPage);
        setAfter(undefined);
        setBefore(startCursor);
      }
      setPage(newPage);
    },
    [page, rowsPerPage]
  );

  const variables: RegularNeedsPeriodsQueryVariables = useMemo(
    () => ({
      month,
      search,
      attachments,
      first,
      last,
      after,
      before,
    }),
    [month, search, attachments, first, last, after, before]
  );

  const extractPagedData = useCallback(
    (connection: Maybe<DataConnection<PeriodsGridItemFragment>> | undefined) => {
      if (!connection) {
        return {
          data: [],
          count: -1,
          startCursor: undefined,
          endCursor: undefined,
          page,
          rowsPerPage,
        };
      }
      const data =
        connection.nodes
          ?.filter((n) => n !== null && n !== undefined)
          .map((n) => n as PeriodsGridItemFragment) ?? [];
      const totalCount = connection.totalCount ?? -1;
      const pageInfo = connection.pageInfo;
      return {
        data,
        count: totalCount > 0 ? totalCount : 0,
        startCursor: pageInfo?.startCursor,
        endCursor: pageInfo?.endCursor,
        page,
        rowsPerPage,
      };
    },
    [page, rowsPerPage]
  );

  const sortableColumns: SortableTableColumn<PeriodsGridItemFragment>[] = useMemo(
    () =>
      columns.map(({ orderBy: _orderBy, ...column }) => ({
        ...column,
        sortable: false,
      })),
    [columns]
  );

  return {
    onSearch,
    attachments,
    onAttachmentsChange,
    onSort,
    onPage,
    variables,
    extractPagedData,
    columns: sortableColumns,
  };
};
